import { ImportStatus, ImportTypes } from '@/gql/graphql';
import {
  Button,
  Collapse,
  Flex,
  HStack,
  Progress,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useImportItem } from '../hooks/useImportItem';
import { FileTransferItem, KVItem, KVTable } from './FileTransferItem';

function Body() {
  const { t } = useTranslation();
  const {
    importItem: { author },
  } = useImportItem();

  return (
    <KVTable>
      <KVItem
        label={t('file_transfer.requested_by')}
        value={author?.fullName || ''}
      />
    </KVTable>
  );
}

function Footer() {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = React.useState(true);
  const {
    importItem: {
      progress,
      status,
      arrayErrors,
      fileTemporaryUrl,
      importWithErrors,
    },
  } = useImportItem();

  return (
    <VStack w="full">
      <HStack w="full" px="2" py="1">
        <Flex gap="2" flex={1}>
          <Button
            onClick={() => {
              window.open(`${fileTemporaryUrl}`);
            }}
            size="sm"
            variant="outline"
          >
            {t('file_transfer.download_file')}
          </Button>
          {!!importWithErrors && (
            <Button
              onClick={() => setCollapsed((old) => !old)}
              size="sm"
              colorScheme="red"
            >
              {t('file_transfer.view_errors')}
            </Button>
          )}
        </Flex>
        <Flex flex="0.5">
          {[
            ImportStatus.Processing,
            ImportStatus.Pending,
            ImportStatus.Validating,
          ].includes(status!) && (
            <Progress
              borderWidth="thin"
              w="full"
              size="lg"
              rounded="md"
              value={progress || 0}
              isIndeterminate={[
                ImportStatus.Pending,
                ImportStatus.Validating,
              ].includes(status!)}
            />
          )}
        </Flex>
      </HStack>
      <Collapse style={{ width: '100%' }} in={!collapsed}>
        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>{t('file_transfer.problem')}</Th>
                <Th>{t('file_transfer.column')}</Th>
                <Th isNumeric>{t('file_transfer.line')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {arrayErrors.errors?.map(
                ({
                  line,
                  column,
                  problem,
                }: {
                  problem: string;
                  column: string;
                  line: number;
                }) => (
                  <Tr>
                    <Td>{problem}</Td>
                    <Td>{column}</Td>
                    <Td isNumeric>{line}</Td>
                  </Tr>
                ),
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Collapse>
    </VStack>
  );
}

export function ImportItem() {
  const { t } = useTranslation();
  const { importItem } = useImportItem();

  const { importType, createdAt, status } = importItem;

  const title = React.useMemo(() => {
    switch (importType) {
      case ImportTypes.Collection:
        return t('file_transfer.import_types.collections');
      case ImportTypes.Formulary:
        return t('file_transfer.import_types.formularies');
      case ImportTypes.Local:
        return t('file_transfer.import_types.locals');
      case ImportTypes.Schedule:
        return t('file_transfer.import_types.schedules');
      case ImportTypes.Task:
        return t('file_transfer.import_types.tasks');
      case ImportTypes.User:
        return t('file_transfer.import_types.users');
      case ImportTypes.Ticket:
        return t('file_transfer.import_types.tickets');
      default:
        return '';
    }
  }, [importType, t]);

  const parsedDate = moment(createdAt).format('LLLL');

  return (
    <FileTransferItem
      title={title}
      subtitle={parsedDate}
      status={status || 'pending'}
      body={<Body />}
      footer={<Footer />}
    />
  );
}
